import React, { useState, useRef, useEffect } from 'react';
import { useBackground, BackgroundColor } from '@/contexts/BackgroundContext';
import { motion, AnimatePresence } from 'framer-motion';

const BackgroundPickerButton: React.FC = () => {
  const { currentBg, changeBg, getDeepBgClasses } = useBackground();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  // Get background color for the button
  const getBgColor = (color: BackgroundColor): string => {
    const colors: Record<BackgroundColor, string> = {
      blue: 'bg-blue-600',
      green: 'bg-green-600',
      purple: 'bg-purple-600',
      red: 'bg-red-600',
      orange: 'bg-orange-600',
      yellow: 'bg-yellow-600'
    };
    return colors[color];
  };
  
  // Get background image for preview
  const getBgImage = (color: BackgroundColor): string => {
    const images: Record<BackgroundColor, string> = {
      blue: '/images/blueBG.jpg',
      green: '/images/greenBG.jpg',
      purple: '/images/purpleBG.jpg',
      red: '/images/redPageBg.jpg',
      orange: '/images/orangeBG.jpg',
      yellow: '/images/yellowBG.jpg'
    };
    return images[color];
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Change background
  const handleColorChange = (color: BackgroundColor) => {
    changeBg(color);
    setIsOpen(false);
  };

  return (
    <div className="dropdown relative" ref={dropdownRef}>
      {/* Color picker button - Apple-style design */}
      <button 
        className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 bg-black/20 backdrop-blur-xl hover:scale-105 border border-white/30"
        onClick={toggleDropdown}
        title="تغيير لون الخلفية"
        aria-label="تغيير لون الخلفية"
      >
        <div className={`w-6 h-6 rounded-full shadow-inner border border-white/40 ${getBgColor(currentBg)}`}></div>
      </button>

      {/* Dropdown menu with Animation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -5, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="dropdown-menu absolute right-0 top-full mt-2 p-4 bg-black/60 backdrop-blur-xl rounded-xl z-50 border border-white/10 w-80 shadow-2xl"
          >
            {/* Arrow pointer */}
            <div className="absolute top-[-8px] right-4 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-black/60"></div>
            
            <h4 className="text-sm font-semibold text-white mb-3">اختر خلفية للواجهة</h4>
            
            <div className="grid grid-cols-3 gap-3">
              {/* Color options with background previews */}
              {(['blue', 'green', 'purple', 'red', 'orange', 'yellow'] as BackgroundColor[]).map((color) => (
                <button
                  key={color}
                  className={`relative group overflow-hidden rounded-xl transition-all duration-300 ${
                    currentBg === color ? 'ring-2 ring-white scale-105' : 'ring-0 hover:ring-1 hover:ring-white/70'
                  }`}
                  onClick={() => handleColorChange(color)}
                  title={color}
                  style={{ aspectRatio: '16/9' }}
                >
                  {/* Background preview image */}
                  <img 
                    src={getBgImage(color)} 
                    alt={color}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Selection overlay */}
                  {currentBg === color && (
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                      <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center">
                        <i className="ph ph-check text-white text-lg"></i>
                      </div>
                    </div>
                  )}
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BackgroundPickerButton;